import React, { useMemo } from 'react'

import cx from 'classnames'
import { Avatar } from 'conversations/components/ConversationAvatar'
import { ErrorBoundary } from 'conversations/components/ErrorBoundary'
import { formatGeolocation } from 'conversations/utils/GeoUtils'
import { stripHTMLFromText } from 'conversations/utils/htmlUtils'
import { getDisplayName } from 'conversations/utils/nameUtils'
import { timeSinceUpdated } from 'conversations/utils/timeStamp'

import { H4 } from '@driftt/tide-core'

import { InsightsConversation } from '../SearchModal/api'

import styles from './styles.module.css'
import sharedStyles from 'conversations/css/shared.module.css'

const PREVIEW_LENGTH = 100
const AUTHOR_TYPE_AGENT = 'AGENT'
const TEAMMATE_PREFIX = 'Teammate: '

interface Props {
  conversation: InsightsConversation
  isHovered: boolean
}

const ConversationSearchResultItem: React.FC<Props> = ({ conversation, isHovered }) => {
  const geolocation = conversation.meta.location
  const formatedLocation =
    geolocation?.city &&
    formatGeolocation({
      city: geolocation.city,
      state: geolocation?.region,
      country: geolocation?.country,
    })

  const endUserDisplayName = getDisplayName(conversation.customer)

  const endUserAvatar = conversation.customer?.avatarUrl
  const endUserTitle = conversation.customer?.employmentTitle
  const companyName = conversation.meta.company?.name

  const lastMessage = conversation?.messages[conversation?.messages.length - 1]
  const conversationPreviewText = lastMessage?.basic.body
  const conversationPreviewAuthorPrefix =
    lastMessage?.author?.type === AUTHOR_TYPE_AGENT ? TEAMMATE_PREFIX : ''

  const conversationPreview =
    stripHTMLFromText(conversationPreviewAuthorPrefix + conversationPreviewText).substring(
      0,
      PREVIEW_LENGTH
    ) + (conversationPreviewText?.length > PREVIEW_LENGTH ? '...' : '')

  const timeDiff = timeSinceUpdated(conversation.meta.newestMessageAt)

  const avatarUser = useMemo(
    () => ({
      id: conversation.conversationId,
      name: endUserDisplayName,
      avatarUrl: endUserAvatar || '',
    }),
    [conversation.conversationId, endUserAvatar, endUserDisplayName]
  )

  return (
    <ErrorBoundary errorState={null}>
      <div
        className={cx(styles.convoResultItem, {
          [styles.hovered]: isHovered,
        })}>
        <Avatar className={styles.convoResultAvatar} user={avatarUser} />
        <div className={styles.convoResultText}>
          <div className={cx(styles.convoResultHeader)}>
            <H4 className={styles.convoResultName} data-private>
              {endUserDisplayName}
            </H4>
            <div className={cx(sharedStyles.separated)}>
              <span className={styles.timeDiff}>{timeDiff}</span>
              {endUserTitle ? (
                <span>
                  {endUserTitle} at {companyName}
                </span>
              ) : (
                <>{companyName && <span>{companyName}</span>} </>
              )}
              {formatedLocation && <span>{formatedLocation}</span>}
            </div>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: conversationPreview ? conversationPreview : '',
            }}
            className={cx(styles.messageBody)}
            data-ocweb-id={'Message'}
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default React.memo(ConversationSearchResultItem)
